import {
	Outlet,
	Link,
	useLoaderData,
	redirect,
	data,
	type LoaderFunctionArgs,
	useLocation,
} from 'react-router'
import { Card, CardContent } from '#app/components/ui/card'
import { Icon } from '#app/components/ui/icon.tsx'
import { Tabs, TabsList, TabsTrigger } from '#app/components/ui/tabs'
import { useIsMobile } from '#app/hooks/use-mobile.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'

export async function loader({ request, params }: LoaderFunctionArgs) {
	if (!params.type) {
		return redirect(`${request.url}/agenda`)
	}

	const meeting = await prisma.meeting.findFirst({
		where: { date: params.date },
		select: { executive: true },
	})

	if (meeting?.executive) {
		const userId = await requireUserId(request)
		const isAdmin = await prisma.user.findFirst({
			where: { id: userId, roles: { some: { name: 'admin' } } },
			select: { id: true },
		})
		if (!isAdmin) throw redirect('/meetings')
	}

	const meetingTypes = [
		{ type: 'agenda', descrption: 'Agenda' },
		{ type: 'minutes', descrption: 'Minutes' },
		{ type: 'financials', descrption: 'Financials' },
	]
	return data({ meetingTypes, date: params.date })
}

export default function MeetingDateRoute() {
	const isMobile = useIsMobile()

	const { date } = useLoaderData<typeof loader>()

	const location = useLocation()
	const path = location.pathname.split('/')
	const paths = ['agenda', 'minutes', 'financials']
	let active = path.reverse().find((p) => paths.includes(p)) || ''

	return (
		<div className="inset-0 flex flex-col px-10">
			<h2 className="text-h2 mb-2 pt-12 lg:mb-6">
				{/* capitalize the first letter */}
				{date} Meeting {active.slice(0, 1).toUpperCase() + active.slice(1)}
			</h2>

			<Tabs defaultValue="account" className="w-full">
				<TabsList className="bg-accent grid w-full grid-cols-3 gap-1">
					<Link to="agenda">
						<TabsTrigger
							value="agenda"
							data-state={active === 'agenda' ? 'active' : 'inactive'}
							className="w-full px-1"
						>
							<Icon className="text-body-md m-2" name="calendar">
								{isMobile ? null : 'Agenda'}
							</Icon>
						</TabsTrigger>
					</Link>
					<Link to="minutes">
						<TabsTrigger
							value="minutes"
							data-state={active === 'minutes' ? 'active' : 'inactive'}
							className="w-full px-1"
						>
							<Icon className="text-body-md m-2" name="file-text">
								{isMobile ? null : 'Minutes'}
							</Icon>
						</TabsTrigger>
					</Link>
					<Link to="financials">
						<TabsTrigger
							value="financials"
							data-state={active === 'financials' ? 'active' : 'inactive'}
							className="w-full px-1"
						>
							<Icon className="text-body-md m-2" name="credits">
								{isMobile ? null : 'Financials'}
							</Icon>
						</TabsTrigger>
					</Link>
				</TabsList>

				<Card className="prose lg:prose-lg dark:prose-invert mb-4 min-h-screen max-w-full">
					<CardContent className="space-y-2">
						<Outlet />
					</CardContent>
				</Card>
			</Tabs>
		</div>
	)
}
